/* eslint-disable max-lines */

import { useArtwork, useArtworkForm, useImagesWarmFastCache } from "@design-lab/entities/artwork";
import { calculatedArtworkDimensions } from "@design-lab/entities/artwork/lib/calculateDimensions";
import { useCurrentComposition } from "@design-lab/entities/composition";
import type { Artwork } from "@design-lab/shared/types/artwork";
import type { Side } from "@design-lab/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { api, type models } from "jiffy-api";

export function useCompleteRefineSession(side: Side) {
  const { getMachineBySide } = useArtwork();
  const { form } = useArtworkForm();
  const { warmCache } = useImagesWarmFastCache();
  const { currentComposition: composition } = useCurrentComposition();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["manualRefine", side, "complete"],
    mutationFn: async (
      params: models.dtf.manualRefine.completeSession.PathParams & {
        image?: Blob;
        manual_edit: boolean;
        remove_background: boolean;
        start_new_edit: boolean;
        ai_edited?: boolean;
      }
    ): Promise<models.dtf.manualRefine.completeSession.Response> => {
      const {
        dtf_artwork_id: dtfArtworkId,
        image,
        manual_edit: manualEdit,
        remove_background: removeBackground,
        start_new_edit: startNewEdit,
        ai_edited: aiEdited
      } = params;

      let raw: unknown;
      if (image) {
        const formData = new FormData();
        formData.append("image", image);
        formData.append("manual_edit", String(manualEdit));
        formData.append("remove_background", String(removeBackground));
        formData.append("start_new_edit", String(startNewEdit));
        if (aiEdited) formData.append("ai_edited", "true");
        raw = await api.dtf.completeRefineSession({ dtf_artwork_id: dtfArtworkId }, formData, {
          raw: true,
          useAxiosForRequest: true,
          headers: { "Content-Type": undefined }
        });
      } else {
        raw = await api.dtf.completeRefineSession(
          { dtf_artwork_id: dtfArtworkId },
          {
            manual_edit: manualEdit,
            remove_background: removeBackground,
            start_new_edit: startNewEdit,
            ...(aiEdited !== undefined && { ai_edited: aiEdited })
          }
        );
      }
      const response = objNamesToCamelCase(raw) as models.dtf.manualRefine.completeSession.Response;

      // Update artwork image and warm the cache before resolving
      const machine = getMachineBySide(side);
      const currentArtwork = machine.state.context.artwork;
      if (currentArtwork && response) {
        const { manualEditUrl: _, manualEditNoTrimUrl: __, ...rest } = currentArtwork;
        const updatedArtwork: Artwork = {
          ...rest,
          ...(response.manualEditUrl && { manualEditUrl: response.manualEditUrl }),
          ...(response.manualEditNoTrimUrl && {
            manualEditNoTrimUrl: response.manualEditNoTrimUrl
          })
        };

        let cachedImage: { image?: HTMLImageElement } | undefined;
        if (manualEdit && updatedArtwork.manualEditUrl) {
          const [img] = await warmCache([updatedArtwork.manualEditUrl]);
          cachedImage = img;
        }

        machine.send({
          type: "reset",
          targetState: "finished",
          context: {
            artwork: updatedArtwork,
            artworkId: machine.state.context.artworkId,
            visitedStates: machine.state.context.visitedStates,
            cacheWarmingStatus: machine.state.context.cacheWarmingStatus
          }
        });

        form.setValue(`info.${side}.editImageManual`, manualEdit);

        // Enhances the recalculations for the refined side(s)
        try {
          const hasValidSize = (placement: "front" | "back" | "frontLeftChest") => {
            const size = form.getValues(`${placement}.size`) as
              | { width?: number; height?: number }
              | undefined;
            return (
              !!size &&
              typeof size.width === "number" &&
              typeof size.height === "number" &&
              size.width > 0 &&
              size.height > 0
            );
          };

          const setMaxSizeIfMissing = (placement: "front" | "back" | "frontLeftChest") => {
            if (hasValidSize(placement)) return;
            const surface = composition?.surfaces?.[placement];
            if (!surface || !cachedImage?.image) return;
            const dims = calculatedArtworkDimensions(cachedImage.image, surface);
            if (dims.maxWidthInches <= 0 || dims.maxHeightInches <= 0) return;
            form.setValue(`${placement}.size`, {
              width: dims.maxWidthInches,
              height: dims.maxHeightInches
            });
          };

          if (side === "back") {
            setMaxSizeIfMissing("back");
          } else {
            setMaxSizeIfMissing("front");
            setMaxSizeIfMissing("frontLeftChest");
          }
        } catch {
          // ignore dimension update errors
        }
      }

      return response;
    },
    onSuccess: (response, variables) => {
      const dtfArtworkId = variables.dtf_artwork_id;
      const responseData = response;

      const updateArtwork = (artwork: models.dtf.getArtworkHistory.Artworks) =>
        artwork.id === dtfArtworkId
          ? {
              ...artwork,
              ordered: {
                ...artwork.ordered,
                manualEditUrl: responseData.manualEditUrl || null,
                manualEditNoTrimUrl: responseData.manualEditNoTrimUrl || null,
                editImageManual: variables.manual_edit
              }
            }
          : artwork;

      // Update infinite query caches - iterate through all pages
      queryClient.setQueriesData(
        { queryKey: ["dtfArtworks", "infinite"] },
        (oldData: { pages: models.dtf.getArtworkHistory.Response[] }) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page: models.dtf.getArtworkHistory.Response) => ({
              ...page,
              artworks: page.artworks?.map(updateArtwork)
            }))
          };
        }
      );

      // Also update regular query caches (non-infinite)
      queryClient.setQueriesData(
        {
          queryKey: ["dtfArtworks"],
          predicate: query => !query.queryKey.includes("infinite")
        },
        (oldData: models.dtf.getArtworkHistory.Response) => {
          if (!oldData?.artworks) return oldData;

          return {
            ...oldData,
            artworks: oldData.artworks.map(updateArtwork)
          };
        }
      );
    }
  });
}
